import { useEffect, useState } from "react";
import { MONTHS, fmtSEK, g } from "../revenue/format";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "../../ui/sheet";

const LINE_META = {
  receipts_tm: { title: "Customer Receipts — T&M", source: "Resource Planning assignments" },
  receipts_projects: { title: "Customer Receipts — Projects", source: "Revenue Planning sources" },
  receipts_services: { title: "Customer Receipts — Services", source: "Revenue Planning sources" },
  receipts_other: { title: "Customer Receipts — Other", source: "Revenue Planning sources" },
  employee_payments: { title: "Employee-related Payments", source: "Personnel Costs" },
  subcontractor_payments: { title: "Subcontractor Payments", source: "Resource Planning (back-to-back customer terms)" },
  indirect_payments: { title: "Indirect Cost Payments", source: "Indirect Costs account assumptions" },
  factoring_payments: { title: "Factoring Payments", source: "Cost Account 6064 assumptions" },
  group_overhead_payments: { title: "Group Overhead Payments", source: "6591 — calculated in the Group build" },
  receipts_pass_through: { title: "Customer Receipts — Pass-through", source: "Operating companies via Group (back-to-back source terms)" },
  pass_through_payments: { title: "Pass-through Payments to Operating Companies", source: "Operating companies via Group (back-to-back source terms)" },
  overhead_recharge_receipts: { title: "Group Overhead Recharge Receipts", source: "Group Overhead allocation (Group settings terms)" },
  vat_settlement: { title: "VAT Settlement", source: "VAT reporting periods" },
  corporate_tax_payments: { title: "Corporate Tax Payments", source: "Finance planning inputs (preliminary tax)" },
  other_cash_items: { title: "Other Cash Items", source: "Finance planning inputs" },
};

const MONTH_FULL = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

function MovementRow({ mv, i }) {
  return (
    <div className="py-2 border-b border-slate-100" data-testid={`cf-dd-mv-${i}`}>
      <div className="flex items-baseline justify-between">
        <div className="text-sm font-medium text-slate-800">{mv.label}</div>
        <div className={`text-sm font-semibold ${mv.gross < 0 ? "text-[#B91C1C]" : "text-slate-900"}`}>{fmtSEK(mv.gross)} kr</div>
      </div>
      <div className="text-xs text-slate-500 mt-0.5">
        Economic: {MONTH_FULL[mv.economic[1] - 1]} {mv.economic[0]}
        {mv.terms_days != null && ` · ${mv.terms_days} days terms`}
        {mv.vat_rate != null && ` · VAT ${mv.vat_rate}%`}
        {` · net ${fmtSEK(mv.net)} + VAT ${fmtSEK(mv.vat)}`}
      </div>
      <div className="text-[10px] uppercase font-semibold text-slate-400 mt-0.5">
        {mv.provenance}
        {(mv.cash[0] !== mv.economic[0] || mv.cash[1] !== mv.economic[1]) &&
          ` · cash lands ${MONTH_FULL[mv.cash[1] - 1]} ${mv.cash[0]}`}
      </div>
    </div>
  );
}

export default function CashFlowDrilldown({ open, onClose, line, month, data, year }) {
  const [m, setM] = useState(month);
  useEffect(() => {
    setM(month);
  }, [month, line, open]);

  if (!data || !line || !LINE_META[line]) {
    return <Sheet open={open} onOpenChange={(v) => !v && onClose()} />;
  }
  const meta = LINE_META[line];
  const movements = g(data.drilldown?.[line] || {}, m) || [];
  const future = (data.future_movements?.[line] || []);
  const cell = g(data.months, m) || {};

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent className="w-[560px] sm:max-w-[560px] overflow-y-auto" data-testid="cf-drilldown">
        <SheetHeader>
          <SheetTitle className="font-heading">{meta.title}</SheetTitle>
          <SheetDescription>
            {data.company_name} — {year}. Source: {meta.source}.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-4 flex flex-nowrap gap-1 overflow-x-auto pb-1" data-testid="cf-dd-months">
          {MONTHS.map((label, i) => (
            <button
              key={i + 1}
              onClick={() => setM(i + 1)}
              className={`px-2 py-1 text-xs rounded-md border transition-colors ${
                m === i + 1 ? "bg-[#0052CC] text-white border-[#0052CC]" : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"
              }`}
              data-testid={`cf-dd-month-${i + 1}`}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="mt-3 text-xs text-slate-500" data-testid="cf-dd-total">
          {MONTH_FULL[m - 1]} total: <span className="font-semibold text-slate-900">{fmtSEK(cell[line] ?? 0)} kr</span>
        </div>

        {line === "vat_settlement" ? (
          <div className="mt-3 border border-slate-200 rounded-lg p-3 space-y-2" data-testid="cf-dd-vat-periods">
            {(data.vat_periods || []).length === 0 && (
              <p className="text-sm text-slate-500">No VAT periods — configure the company's VAT reporting under Administration → Companies.</p>
            )}
            {(data.vat_periods || []).map((p, i) => (
              <div key={i} className="py-2 border-b border-slate-100 last:border-0" data-testid={`cf-dd-vat-period-${i}`}>
                <div className="flex items-baseline justify-between">
                  <div className="text-sm font-semibold text-slate-900">{p.label} <span className="text-xs font-normal text-slate-400">({p.frequency.toLowerCase()})</span></div>
                  <div className={`text-sm font-bold ${p.net_vat > 0 ? "text-[#B91C1C]" : "text-[#047857]"}`}>
                    {fmtSEK(p.net_vat)} kr {p.direction === "PAYABLE" ? "payable" : p.direction === "RECEIVABLE" ? "receivable" : ""}
                  </div>
                </div>
                <div className="text-xs text-slate-500 mt-0.5">
                  Output VAT {fmtSEK(p.output_vat)} − Input VAT {fmtSEK(p.input_vat)}
                  {p.settlement ? ` · cash settles ${MONTH_FULL[p.settlement[1] - 1]} ${p.settlement[0]}` : " · settlement month not configured"}
                </div>
              </div>
            ))}
          </div>
        ) : line === "corporate_tax_payments" ? (
          <div className="mt-3 border border-slate-200 rounded-lg p-3" data-testid="cf-dd-tax">
            <div className="text-xs text-slate-500 mb-2">
              Suggested annual corporate tax (informational): basis {fmtSEK(data.tax?.basis)} kr × {data.tax?.rate ?? "—"}% = {data.tax?.suggested_tax == null ? "—" : `${fmtSEK(data.tax.suggested_tax)} kr`}.
              Cash payments are Finance planning inputs, entered as manual cash items (category TAX).
            </div>
            {movements.length === 0 && <p className="text-sm text-slate-500">No tax payments entered for {MONTH_FULL[m - 1]}.</p>}
            {movements.map((mv, i) => <MovementRow key={i} mv={mv} i={i} />)}
          </div>
        ) : (
          <div className="mt-3 border border-slate-200 rounded-lg p-3" data-testid="cf-dd-movements">
            {movements.length === 0 && <p className="text-sm text-slate-500">No cash movements in {MONTH_FULL[m - 1]}.</p>}
            {movements.map((mv, i) => <MovementRow key={i} mv={mv} i={i} />)}
          </div>
        )}

        {future.length > 0 && (
          <div className="mt-4" data-testid="cf-dd-future">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">
              From {year} economics — cash lands after December {year}
            </div>
            <div className="border border-slate-200 rounded-lg p-3">
              {future.map((mv, i) => <MovementRow key={i} mv={mv} i={i} />)}
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
